import * as React from "react";
import { OPERATORS_REQUIRING_TWO_VALUES, queryProperties } from "../../../services/utils";
import { InfoTooltip } from "../../../components/InfoTooltip";
import { Alert, Button, Input, Select } from "antd";
import clsx from "clsx";
import { CheckCircleOutlined, DownOutlined, MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { clearActive, selectQuery, setActive } from "../../../store/querySlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { isEqual } from "lodash";
import {
  addTerm,
  clear,
  removeTerm,
  setMatch,
  setSortBy,
  setSortOrder,
  updateTermKey,
  updateTermOp,
  updateTermType,
  updateTermValue,
} from "../../../store/queryInputSlice";
import { ListFieldLabels } from "../../../pages/gvc/listFieldLabels";
import {
  getDefaultQueryInputTerm,
  inputToQuery,
  QueryInputTermTypeOptions,
  QueryTermOperatorOptions,
} from "../../../schema/types/query/input";
import { Kind } from "../../../schema/types/base";

export interface QueryTableItem {
  id: string;
  term: string;
  key: string;
  operator: string;
  value: string;
}
interface CustomTableCPLNQueryProps {
  tableId: string;
  kind: Kind;
  isLoading?: boolean;
  onQuery: (query: any) => void;
}
export function CustomTableCPLNQuery({ tableId, kind, isLoading, onQuery }: CustomTableCPLNQueryProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isApplied, setIsApplied] = React.useState(false);

  const query = useAppSelector(selectQuery);
  const dispatch = useAppDispatch();

  function getIsQueryDirty(): boolean {
    if (!query.active) {
      return query.draft.terms.length > 0;
    }
    return !isEqual(query.active, inputToQuery(query.draft, kind));
  }
  const isQueryDirty = getIsQueryDirty();

  function getIsQueryInvalid(): boolean {
    if (query.draft.terms.length < 1) {
      return true;
    }
    return query.draft.terms.some((term) => {
      if (OPERATORS_REQUIRING_TWO_VALUES.includes(term.op)) {
        return !term.value;
      }
      return !term.key;
    });
  }
  const isQueryValid = !getIsQueryInvalid();

  const allowedProperties: string[] = (queryProperties as any)[kind] || [];
  const hasAllowedProperties = allowedProperties.length > 0;
  const propertyOptions = allowedProperties.map((p) => ({ label: p, value: p }));

  const queryModalRef = React.useRef<HTMLDivElement>(null as any);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: PointerEvent) => {
      const target = e.target as HTMLElement;

      // Check if click is inside the query modal
      if (queryModalRef.current?.contains(target)) {
        return;
      }

      // Check if click is inside Ant Design portals (Select, DatePicker, etc.)
      if (target.closest(".ant-select-dropdown, .ant-picker-dropdown, .ant-dropdown")) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener("pointerup", handleClickOutside);
    return () => document.removeEventListener("pointerup", handleClickOutside);
  }, [isOpen]);

  function handleClearQuery() {
    onQuery(null);
    setIsApplied(false);
    dispatch(clearActive());
    dispatch(clear());
    setIsOpen(false);
    localStorage.removeItem(`${tableId}-query`);
  }

  function handleApplyQuery() {
    setIsApplied(true);

    const queryApi = inputToQuery(query.draft, kind);
    onQuery(queryApi);
    dispatch(setActive(queryApi));
    const queryStr = JSON.stringify(queryApi);
    localStorage.setItem(`${tableId}-query`, queryStr);

    setIsOpen(false);
  }

  return (
    <div
      ref={queryModalRef}
      className={`${isOpen && "query-before"} relative flex items-center justify-center`}
      style={{ width: 110 }}
    >
      <Button
        type={isApplied ? "primary" : "default"}
        className={clsx({ action: isApplied })}
        onClick={() => setIsOpen((x) => !x)}
        style={{ width: 110 }}
        icon={isApplied ? <CheckCircleOutlined /> : <DownOutlined />}
        iconPosition="end"
      >
        {isApplied ? "Queried" : "Query"}
      </Button>
      {isOpen && (
        <div className="query-modal absolute flex flex-col p-4 z-10 shadow-lg">
          <div className="flex justify-between">
            <div className="flex flex-col">
              <div className="flex gap-2">
                <div className="flex flex-col">
                  <div className="mb-2">Sort By</div>
                  <Select
                    style={{ width: 200, minWidth: 200 }}
                    className="basis-0 flex-grow"
                    placeholder={"Sort By"}
                    onChange={(value) => dispatch(setSortBy(value))}
                    options={[{ label: "-", value: "" }, ...allowedProperties.map((p) => ({ label: p, value: p }))]}
                    value={query.draft.sort.isEnabled ? query.draft.sort.by : ""}
                  />
                </div>
                {query.draft.sort.isEnabled ? (
                  <div className="flex flex-col">
                    <div className="mb-2">Sort Order</div>
                    <Select
                      style={{ width: 130, minWidth: 130, maxWidth: 130 }}
                      size="small"
                      className="basis-0 flex-grow"
                      placeholder={"Sort Order"}
                      onChange={(value) => dispatch(setSortOrder(value))}
                      options={[
                        { label: "Ascending", value: "asc" },
                        { label: "Descending", value: "desc" },
                      ]}
                      value={query.draft.sort.order}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center mb-2">
                <div>Match Terms By</div>
                <InfoTooltip
                  style={{ color: "var(--color-label)" }}
                  title={[
                    `'All' means all terms should match`,
                    `'Any' means any of the terms should match`,
                    `'None' means none of these terms should match`,
                  ]}
                />
              </div>
              <div className="flex items-center">
                <Button
                  size={"small"}
                  type={query.draft.match === "all" ? "primary" : "default"}
                  onClick={() => dispatch(setMatch("all"))}
                  style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                >
                  All
                </Button>
                <Button
                  size={"small"}
                  type={query.draft.match === "any" ? "primary" : "default"}
                  onClick={() => dispatch(setMatch("any"))}
                  style={{ borderRadius: 0 }}
                >
                  Any
                </Button>
                <Button
                  size={"small"}
                  type={query.draft.match === "none" ? "primary" : "default"}
                  onClick={() => dispatch(setMatch("none"))}
                  style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                >
                  None
                </Button>
              </div>
            </div>
          </div>
          <div className="mt-4">Terms</div>
          {query.draft.terms.length > 0 ? (
            <ListFieldLabels columnCount={4} fieldLabels={["Term", "Key", "Operator", "Value"]} />
          ) : null}
          {query.draft.terms.map((term, index) => {
            return (
              <div key={`${term.termType}-${index}`} className="flex items-center gap-2 mb-1">
                <div className="flex-grow grid grid-cols-4 gap-2">
                  <Select
                    value={term.termType}
                    onChange={(value) => dispatch(updateTermType({ id: term.id, termType: value }))}
                    options={QueryInputTermTypeOptions}
                  />
                  {term.termType === "property" && hasAllowedProperties ? (
                    <Select
                      value={term.key}
                      onChange={(value) => dispatch(updateTermKey({ id: term.id, key: value }))}
                      options={propertyOptions}
                    />
                  ) : (
                    <Input
                      value={term.key}
                      placeholder={
                        term.termType === "property" ? "Property" : term.termType === "tag" ? "Tag Key" : "Rel"
                      }
                      onChange={(e) => dispatch(updateTermKey({ id: term.id, key: e.target.value }))}
                      status={!term.key ? "error" : ""}
                    />
                  )}
                  <Select
                    value={term.op}
                    onChange={(value) => dispatch(updateTermOp({ id: term.id, op: value }))}
                    options={QueryTermOperatorOptions}
                  />
                  <Input
                    value={term.value}
                    disabled={!OPERATORS_REQUIRING_TWO_VALUES.includes(term.op)}
                    onChange={(e) => dispatch(updateTermValue({ id: term.id, value: e.target.value }))}
                    status={OPERATORS_REQUIRING_TWO_VALUES.includes(term.op) && !term.value ? "error" : ""}
                  />
                </div>
                <Button
                  onClick={() => dispatch(removeTerm(term.id))}
                  type={`text`}
                  danger
                  icon={<MinusCircleOutlined />}
                  style={{ minWidth: 32 }}
                />
              </div>
            );
          })}
          <Button
            type={`dashed`}
            icon={<PlusCircleOutlined />}
            onClick={() => dispatch(addTerm(getDefaultQueryInputTerm()))}
            block
          >
            Add
          </Button>
          {!isQueryValid && query.draft.terms.length > 0 ? (
            <Alert className="my-1" type={`warning`} message={`Query is not valid`} />
          ) : null}
          <div className="flex mt-8 gap-4">
            <Button
              className="flex-grow"
              disabled={(!isQueryDirty && !isApplied) || isLoading}
              type={"default"}
              onClick={handleClearQuery}
            >
              Clear
            </Button>
            <Button
              className="flex-grow"
              disabled={!isQueryDirty || !isQueryValid}
              loading={isLoading}
              type={"primary"}
              onClick={handleApplyQuery}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
